import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Search, Package, CheckCircle, Truck, Inbox, Star, Phone, MapPin, Droplets, Copy, Download, CreditCard, QrCode, ZoomIn, MessageCircle, Upload, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { publicApi, paymentProofsApi } from '../services/api';
import { formatCurrency, formatDateTime } from '../utils/helpers';
import { toast } from 'sonner';

const STEPS = [
  { key: 'masuk', icon: Inbox, label: 'Masuk', labelEn: 'Received' },
  { key: 'proses_cuci', icon: Truck, label: 'Proses Cuci', labelEn: 'Washing' },
  { key: 'siap_diambil', icon: Package, label: 'Siap Diambil', labelEn: 'Ready' },
  { key: 'selesai', icon: CheckCircle, label: 'Selesai', labelEn: 'Done' },
];

const STATUS_IDX = (columns) => {
  const map = {};
  columns.forEach((col, idx) => { map[col.key] = idx; });
  return map;
};

function Stepper({ status, businessType, lang }) {
  // Build steps from business type's kanban columns
  const LAUNDRY_STEPS = [
    { key: 'masuk', icon: Inbox, label: 'Masuk', labelEn: 'Received' },
    { key: 'proses_cuci', icon: Truck, label: 'Diproses', labelEn: 'Processing' },
    { key: 'siap_diambil', icon: Package, label: 'Siap Diambil', labelEn: 'Ready' },
    { key: 'selesai', icon: CheckCircle, label: 'Selesai', labelEn: 'Done' },
  ];
  const RENTAL_STEPS = [
    { key: 'masuk', icon: Inbox, label: 'Masuk', labelEn: 'Received' },
    { key: 'serah_terima', icon: Truck, label: 'Serah Terima', labelEn: 'Handover' },
    { key: 'sedang_disewa', icon: Package, label: 'Sedang Disewa', labelEn: 'In Use' },
    { key: 'selesai', icon: CheckCircle, label: 'Selesai', labelEn: 'Done' },
  ];
  const SERVIS_STEPS = [
    { key: 'masuk', icon: Inbox, label: 'Masuk', labelEn: 'Received' },
    { key: 'diagnosa', icon: Truck, label: 'Diagnosa', labelEn: 'Diagnosis' },
    { key: 'sedang_dikerjakan', icon: Package, label: 'Dikerjakan', labelEn: 'In Progress' },
    { key: 'selesai', icon: CheckCircle, label: 'Selesai', labelEn: 'Done' },
  ];
  const APPT_STEPS = [
    { key: 'masuk', icon: Inbox, label: 'Masuk', labelEn: 'Received' },
    { key: 'konfirmasi', icon: Truck, label: 'Dikonfirmasi', labelEn: 'Confirmed' },
    { key: 'sedang_dikerjakan', icon: Package, label: 'Dilayani', labelEn: 'In Service' },
    { key: 'selesai', icon: CheckCircle, label: 'Selesai', labelEn: 'Done' },
  ];

  const stepsMap = {
    laundry: LAUNDRY_STEPS,
    rental: RENTAL_STEPS,
    servis_elektronik: SERVIS_STEPS,
    servis_kendaraan: SERVIS_STEPS,
    mua: APPT_STEPS,
    penjahit: APPT_STEPS,
    lainnya: LAUNDRY_STEPS,
  };
  const steps = stepsMap[businessType] || stepsMap.lainnya;
  const currentIdx = steps.findIndex(s => s.key === status);
  const idx = currentIdx === -1 ? 0 : currentIdx;
  return (
      <div className="relative flex items-center justify-between w-full">
      <div className="absolute left-0 right-0 top-5 h-0.5 bg-slate-200 z-0" />
      <div
        className="absolute left-0 top-5 h-0.5 bg-[#0f6c44] z-0 transition-all duration-700"
        style={{ width: `${(idx / (steps.length - 1)) * 100}%` }}
      />
      {steps.map((step, i) => {
        const done = i <= idx;
        const active = i === idx;
        return (
          <div key={step.key} className="relative z-10 flex flex-col items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
              done ? 'bg-[#0f6c44] border-[#0f6c44]' : 'bg-white border-slate-300'
            } ${active ? 'shadow-lg shadow-emerald-200 scale-110' : ''}`}>
              <step.icon size={18} className={done ? 'text-white' : 'text-slate-400'} />
            </div>
            <span className={`text-xs font-semibold text-center leading-tight ${done ? 'text-[#0f6c44]' : 'text-slate-400'}`}>
              {lang === 'id' ? step.label : step.labelEn}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function normalizeWaPhone(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('62')) return digits;
  if (digits.startsWith('0')) return '62' + digits.slice(1);
  if (digits.startsWith('8')) return '62' + digits;
  return digits;
}

function PaymentConfirmSection({ orderId, remaining, t }) {
  const [show, setShow] = useState(false);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: () => paymentProofsApi.submit(orderId, parseFloat(amount), notes, file),
    onSuccess: () => { setSubmitted(true); },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Gagal mengirim konfirmasi'),
  });

  if (remaining <= 0) return null;

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center">
        <CheckCircle2 size={36} className="text-green-500 mx-auto mb-2" />
        <p className="font-bold text-green-800">{t('Konfirmasi Terkirim!', 'Confirmation Sent!')}</p>
        <p className="text-xs text-green-600 mt-1">{t('Toko sedang memverifikasi pembayaran Anda', 'Store is verifying your payment')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Upload size={15} className="text-blue-600" />
        </div>
        <div>
          <p className="font-bold text-slate-800 text-sm font-manrope">{t('Konfirmasi Pembayaran', 'Confirm Payment')}</p>
          <p className="text-xs text-slate-500">{t('Sudah bayar? Upload bukti transfer di sini', 'Already paid? Upload your transfer proof here')}</p>
        </div>
        {show && <button onClick={() => setShow(false)} className="ml-auto p-1 rounded-lg hover:bg-slate-100"><X size={16} className="text-slate-400" /></button>}
      </div>

      {!show ? (
        <button onClick={() => setShow(true)} data-testid="konfirmasi-pembayaran-btn"
          className="w-full py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all">
          {t('Konfirmasi Pembayaran', 'Confirm Payment')}
        </button>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">{t('Nominal yang dibayar *', 'Amount paid *')}</label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder={`Maks. Rp ${Math.round(remaining).toLocaleString('id-ID')}`}
              data-testid="proof-amount-input"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 bg-slate-50" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">{t('Catatan (opsional)', 'Notes (optional)')}</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder={t('Contoh: Transfer via BCA pukul 14:30', 'e.g. Transferred via BCA at 2:30pm')}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 bg-slate-50" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">{t('Bukti Transfer (JPG/PNG, maks 4MB)', 'Transfer Proof (JPG/PNG, max 4MB)')}</label>
            {file ? (
              <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-xl border border-blue-200">
                <CheckCircle2 size={14} className="text-blue-600 flex-shrink-0" />
                <span className="text-xs text-blue-700 truncate flex-1">{file.name}</span>
                <button onClick={() => setFile(null)} className="p-1 hover:bg-blue-100 rounded"><X size={12} className="text-blue-500" /></button>
              </div>
            ) : (
              <label className="flex flex-col items-center gap-1.5 border-2 border-dashed border-slate-200 rounded-xl p-4 cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-all">
                <input type="file" accept="image/jpeg,image/png,image/jpg" className="hidden"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f && f.size > 4 * 1024 * 1024) { toast.error('Ukuran file maksimal 4MB'); return; }
                    if (f) setFile(f);
                  }} />
                <Upload size={20} className="text-slate-300" />
                <p className="text-xs text-slate-400">{t('Pilih file bukti transfer', 'Select transfer proof file')}</p>
              </label>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={() => setShow(false)} className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200">
              {t('Batal', 'Cancel')}
            </button>
            <button onClick={() => mutation.mutate()} disabled={mutation.isPending || !amount}
              data-testid="submit-proof-btn"
              className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-all flex items-center justify-center gap-1">
              {mutation.isPending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : t('Kirim Konfirmasi', 'Send Confirmation')}
            </button>
          </div>
          <p className="text-xs text-slate-400 text-center">{t('Toko akan memverifikasi dan memperbarui status pembayaran Anda', 'Store will verify and update your payment status')}</p>
        </div>
      )}
    </div>
  );
}

const PM_LABELS = {
  bca: { label: 'BCA', color: 'bg-blue-100 text-blue-700', fieldLabel: 'No. Rekening' },
  bni: { label: 'BNI', color: 'bg-orange-100 text-orange-700', fieldLabel: 'No. Rekening' },
  bri: { label: 'BRI', color: 'bg-sky-100 text-sky-700', fieldLabel: 'No. Rekening' },
  mandiri: { label: 'Mandiri', color: 'bg-yellow-100 text-yellow-700', fieldLabel: 'No. Rekening' },
  gopay: { label: 'GoPay', color: 'bg-teal-100 text-teal-700', fieldLabel: 'No. HP / Akun' },
  dana: { label: 'DANA', color: 'bg-blue-100 text-blue-700', fieldLabel: 'No. HP / Akun' },
  shopee_pay: { label: 'ShopeePay', color: 'bg-orange-100 text-orange-700', fieldLabel: 'No. HP / Akun' },
};

function PaymentMethodsSection({ paymentMethods, remaining, t }) {
  const [qrisZoom, setQrisZoom] = useState(false);
  if (!paymentMethods || remaining <= 0) return null;
  const activeKeys = Object.keys(paymentMethods).filter(k => paymentMethods[k]?.enabled);
  if (activeKeys.length === 0) return null;

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${label} berhasil disalin!`))
      .catch(() => toast.error('Gagal menyalin'));
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="cara-pembayaran-section">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <CreditCard size={15} className="text-[#0f6c44]" />
        </div>
        <div>
          <p className="font-bold text-slate-800 text-sm font-manrope">{t('Cara Pembayaran', 'How to Pay')}</p>
          <p className="text-xs text-slate-500">{t('Transfer sisa tagihan', 'Transfer remaining balance')}: <span className="font-bold text-orange-600">{formatCurrency(remaining)}</span></p>
        </div>
      </div>

      <div className="space-y-3">
        {activeKeys.map(key => {
          const method = paymentMethods[key];
          if (key === 'qris') {
            return (
              <div key={key} className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <QrCode size={14} className="text-purple-700" />
                  </div>
                  <p className="font-semibold text-sm text-slate-800">QRIS</p>
                </div>
                {method.qris_image_url ? (
                  <div className="flex flex-col items-center gap-2">
                    <img
                      src={method.qris_image_url}
                      alt="QRIS"
                      onClick={() => setQrisZoom(true)}
                      data-testid="qris-image"
                      className="w-48 h-48 object-contain border border-slate-200 rounded-xl cursor-zoom-in hover:shadow-md transition-shadow"
                    />
                    <button onClick={() => setQrisZoom(true)}
                      className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600">
                      <ZoomIn size={12} />{t('Perbesar', 'Zoom in')}
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 text-center py-2">{t('Gambar QRIS belum diupload', 'QRIS image not uploaded yet')}</p>
                )}
              </div>
            );
          }
          const meta = PM_LABELS[key];
          if (!meta) return null;
          return (
            <div key={key} className="border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-extrabold flex-shrink-0 ${meta.color}`}>
                    {key === 'shopee_pay' ? 'SPy' : meta.label.slice(0, 3).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-slate-800">{meta.label}</p>
                    <p className="text-xs text-slate-500">{meta.fieldLabel}: <span className="font-mono font-bold text-slate-700">{method.account_number || '-'}</span></p>
                    {method.account_name && <p className="text-xs text-slate-400">a.n. {method.account_name}</p>}
                  </div>
                </div>
                <button onClick={() => copyText(method.account_number || '', meta.label)}
                  data-testid={`copy-${key}`}
                  className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 transition-colors flex-shrink-0 ml-2">
                  <Copy size={11} />{t('Salin', 'Copy')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-slate-400 mt-4 text-center">
        {t('Setelah transfer, hubungi toko untuk konfirmasi pembayaran', 'After transfer, contact the store to confirm payment')}
      </p>

      {/* QRIS Zoom Modal */}
      {qrisZoom && paymentMethods.qris?.qris_image_url && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setQrisZoom(false)}>
          <div className="bg-white rounded-2xl p-4 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <p className="font-bold text-center mb-3 text-slate-800">QRIS</p>
            <img src={paymentMethods.qris.qris_image_url} alt="QRIS" className="w-full object-contain rounded-xl" />
            <button onClick={() => setQrisZoom(false)}
              className="w-full mt-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200">
              {t('Tutup', 'Close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicTrackingPage() {
  const { tracking_code: paramCode } = useParams();
  const navigate = useNavigate();
  const [inputCode, setInputCode] = useState('');
  const [searchCode, setSearchCode] = useState(paramCode || '');
  const [lang, setLang] = useState('id');

  const BASE = process.env.REACT_APP_BACKEND_URL;

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['public-track', searchCode],
    queryFn: () => publicApi.track(searchCode).then(r => r.data),
    enabled: !!searchCode,
    retry: false,
  });

  const handleSearch = (e) => {
    e.preventDefault();
    if (inputCode.trim()) setSearchCode(inputCode.trim().toUpperCase());
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/track/${order.tracking_code}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link berhasil disalin!')).catch(() => toast.error('Gagal menyalin link'));
  };

  const t = (id, en) => lang === 'id' ? id : en;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#0f6c44] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-sm font-manrope">K</span>
            </div>
            <span className="font-bold text-slate-800 font-manrope">KasirWA</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setLang(l => l === 'id' ? 'en' : 'id')}
              className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors">
              {lang === 'id' ? '🇬🇧 EN' : '🇮🇩 ID'}
            </button>
            <button onClick={() => navigate('/login')}
              className="text-xs text-[#0f6c44] font-semibold hover:underline">
              {t('Login', 'Login')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Search Box */}
        <div className="text-center mb-2">
          <h1 className="text-2xl font-extrabold text-slate-900 font-manrope">
            {t('Lacak Pesanan', 'Track Order')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {t('Masukkan kode tracking yang ada di nota pesanan Anda', 'Enter the tracking code from your receipt')}
          </p>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={inputCode}
              onChange={e => setInputCode(e.target.value.toUpperCase())}
              placeholder={t('Contoh: ABX1234CD', 'e.g. ABX1234CD')}
              className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-xl text-sm font-mono font-semibold tracking-wider focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] shadow-sm"
            />
          </div>
          <button type="submit"
            className="px-6 py-3.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm whitespace-nowrap">
            {t('Lacak', 'Track')}
          </button>
        </form>

        {/* Loading */}
        {isLoading && (
          <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center shadow-sm">
            <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0f6c44] rounded-full animate-spin mx-auto mb-3" />
            <p className="text-slate-400 text-sm">{t('Mencari pesanan...', 'Searching...')}</p>
          </div>
        )}

        {/* Error */}
        {error && !isLoading && (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-6 text-center">
            <p className="text-red-600 font-semibold text-sm">{t('Pesanan tidak ditemukan', 'Order not found')}</p>
            <p className="text-red-400 text-xs mt-1">{t('Periksa kembali kode tracking Anda', 'Please check your tracking code')}</p>
          </div>
        )}

        {/* Result */}
        {order && !isLoading && (
          <div className="space-y-4 animate-fade-in">
            {/* Store info */}
            {order.store && (
              <div className="bg-[#0f6c44] text-white rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="font-bold text-lg font-manrope">{order.store.name}</p>
                    {order.store.address && <p className="text-white/80 text-sm mt-0.5 flex items-center gap-1"><MapPin size={13} />{order.store.address}</p>}
                    {order.store.phone && <p className="text-white/80 text-sm mt-0.5 flex items-center gap-1"><Phone size={13} />{order.store.phone}</p>}
                  </div>
                  {order.store.phone && (
                    <a
                      href={`https://wa.me/${normalizeWaPhone(order.store.phone)}`}
                      target="_blank" rel="noreferrer"
                      data-testid="wa-store-btn"
                      className="flex flex-col items-center gap-1 bg-white/20 hover:bg-white/30 active:scale-95 transition-all rounded-2xl p-3 flex-shrink-0 min-w-[60px]"
                    >
                      <MessageCircle size={24} className="text-white" />
                      <span className="text-white/90 text-xs font-semibold">WA</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Status card */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="font-mono text-lg font-extrabold text-[#0f6c44]">{order.tracking_code}</span>
                  <p className="text-slate-500 text-sm mt-0.5">{order.customer_name || '-'}</p>
                </div>
                <span className={`text-sm font-bold px-3 py-1.5 rounded-full ${
                  order.status === 'selesai' ? 'bg-green-100 text-green-700' :
                  order.status === 'siap_diambil' ? 'bg-emerald-100 text-emerald-700' :
                  order.status === 'proses_cuci' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {order.status_label}
                </span>
              </div>

              {/* Progress bar stepper */}
              <Stepper status={order.status} businessType={order.store?.business_type || 'laundry'} lang={lang} />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button onClick={handleCopyLink}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors">
                <Copy size={15} />{t('Salin Link', 'Copy Link')}
              </button>
              <a href={`${BASE}/api/orders/${order.id}/receipt?format=a4`}
                target="_blank" rel="noreferrer"
                data-testid="download-nota-btn"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-medium hover:bg-[#0c5636] transition-colors">
                <Download size={15} />{t('Download Nota', 'Download Receipt')}
              </a>
            </div>

            {/* Order details */}
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-4 font-manrope">{t('Detail Pesanan', 'Order Details')}</h3>
              <div className="space-y-2 mb-4">
                {(order.items || []).map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-slate-50 last:border-0">
                    <span className="text-sm text-slate-700">{item.product_name} <span className="text-slate-400">({item.quantity} {item.unit})</span></span>
                    <span className="text-sm font-semibold text-slate-800">{formatCurrency(item.subtotal)}</span>
                  </div>
                ))}
              </div>

              {(order.parfum || order.shelf_location) && (
                <div className="flex gap-4 mb-4">
                  {order.parfum && (
                    <span className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg">
                      <Droplets size={13} className="text-blue-400" />{order.parfum}
                    </span>
                  )}
                  {order.shelf_location && (
                    <span className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg">
                      <MapPin size={13} className="text-slate-400" />{order.shelf_location}
                    </span>
                  )}
                </div>
              )}

              <div className="border-t border-slate-100 pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total</span>
                  <span className="font-bold text-slate-800">{formatCurrency(order.total_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">{t('Terbayar', 'Paid')}</span>
                  <span className="font-semibold text-green-600">{formatCurrency(order.paid_amount)}</span>
                </div>
                {(order.remaining_amount || 0) > 0 && (
                  <div className="flex justify-between text-sm pt-2 border-t border-slate-100">
                    <span className="font-semibold text-slate-700">{t('Sisa Tagihan', 'Remaining')}</span>
                    <span className="font-bold text-orange-600">{formatCurrency(order.remaining_amount)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Cara Pembayaran */}
            <PaymentMethodsSection
              paymentMethods={order.store?.payment_methods}
              remaining={order.remaining_amount || 0}
              t={t}
            />

            {/* Konfirmasi Pembayaran */}
            <PaymentConfirmSection
              orderId={order.id}
              remaining={order.remaining_amount || 0}
              t={t}
            />

            {/* Timeline */}
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <p className="text-xs text-slate-400">{t('Dibuat', 'Created')}: {formatDateTime(order.created_at)}</p>
              {order.status === 'selesai' && (
                <div className="flex items-center gap-2 mt-3 bg-green-50 rounded-xl p-3">
                  <Star size={16} className="text-yellow-500" />
                  <p className="text-sm font-semibold text-green-700">{t('Terima kasih telah mempercayai kami!', 'Thank you for your trust!')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state (no search yet) */}
        {!searchCode && !isLoading && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Package size={28} className="text-[#0f6c44]" />
            </div>
            <p className="text-slate-500 text-sm">{t('Masukkan kode tracking untuk melihat status pesanan', 'Enter tracking code to see order status')}</p>
          </div>
        )}
      </main>
    </div>
  );
}
